import { IMPORT_FIELDS, type ImportEntity } from '../schemas/import';

/**
 * Matching a shop's CSV column names to ours (PRD v2 §7 V4). Lives in shared rather than in
 * the import page because it is domain knowledge — what shops call their columns — not
 * presentation, and because it is the kind of string matching that needs tests.
 */
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * What shops actually call these columns. Worth a hand-written list: the alternative is a
 * cleverer string matcher, and a cleverer matcher is exactly what produced the bug this
 * replaces — "Customer name" matched the header "ST", because "st" is a substring of
 * "cu-st-omername". Every shop would have imported a customer list full of people called
 * "OH".
 */
const ALIASES: Record<string, string[]> = {
  displayName: ['name', 'customer', 'customername', 'client', 'clientname', 'account'],
  companyName: ['company', 'business', 'organization'],
  kind: ['type', 'category', 'customertype'],
  email: ['email', 'emailaddress'],
  phone: ['phone', 'phonenumber', 'telephone', 'mobile', 'cell'],
  city: ['city', 'town'],
  state: ['state', 'st', 'province'],
  address1: ['address', 'street', 'streetaddress', 'serviceaddress', 'address1'],
  propertyLabel: ['propertyname', 'location', 'site'],
  contactFirstName: ['firstname', 'first', 'contactfirst'],
  contactLastName: ['lastname', 'last', 'surname', 'contactlast'],
  contactEmail: ['contactemail'],
  contactPhone: ['contactphone'],
  leadSource: ['leadsource', 'source', 'referredby', 'howtheyfoundus', 'referral'],
  name: ['name', 'item', 'itemname', 'service', 'product', 'description'],
  unitPrice: ['price', 'unitprice', 'rate', 'amount', 'sellprice'],
  sku: ['sku', 'stockcode', 'itemcode'],
  unitCost: ['cost', 'unitcost', 'costprice'],
  openingQty: ['openingquantity', 'openingqty', 'openingcount'],
  stockLocation: ['stocklocation', 'stocklocationid', 'location'],
  description: ['description', 'details', 'notes'],
  unitLabel: ['unit', 'uom', 'each'],
  taxable: ['taxable', 'tax'],
  category: ['category', 'group', 'type'],
};

/** How well one header answers one field. 0 = not at all. */
function score(fieldKey: string, fieldLabel: string, header: string): number {
  const h = norm(header);
  if (!h || (fieldKey === 'unitPrice' && h.includes('cost'))) return 0;
  const wants = [norm(fieldKey), norm(fieldLabel), ...(ALIASES[fieldKey] ?? [])];
  let best = 0;
  for (const w of wants) {
    if (!w) continue;
    if (h === w) return 3; // exact — nothing beats it
    // Substring matching ONLY where the shorter side is long enough to mean something.
    // Four characters is the floor that keeps "st" out of "customername".
    if (w.length >= 4 && h.includes(w)) best = Math.max(best, 2);
    if (h.length >= 4 && w.includes(h)) best = Math.max(best, 1);
  }
  return best;
}

/**
 * Best-effort auto-map. Every header is claimed at most ONCE, by whichever field wants it
 * most — otherwise "Email" ends up answering both Email and Contact email and the shop
 * imports the same address twice. Ties break toward the earlier field, which is the more
 * important one in every IMPORT_FIELDS list.
 */
export function autoMapColumns(
  entity: ImportEntity,
  headers: string[],
): Record<string, string> {
  const specs = IMPORT_FIELDS[entity];
  const pairs: { key: string; header: string; score: number; rank: number }[] = [];
  specs.forEach((spec, rank) => {
    for (const header of headers) {
      const s = score(spec.key, spec.label, header);
      if (s > 0) pairs.push({ key: spec.key, header, score: s, rank });
    }
  });
  pairs.sort((a, b) => b.score - a.score || a.rank - b.rank);

  const map: Record<string, string> = {};
  const usedHeaders = new Set<string>();
  for (const p of pairs) {
    if (map[p.key] || usedHeaders.has(p.header)) continue;
    map[p.key] = p.header;
    usedHeaders.add(p.header);
  }
  return map;
}
